<script setup lang="ts">
import { computed } from 'vue'
import AddressSearch from '../AddressSearch.vue'
import {
    CheckpointInputType,
    type JawgSuggestedFeature
} from '../../../../../interfaces/map'

const { type, hasReachedStopsLimit = false } = defineProps<{
    index: number
    type: CheckpointInputType
    hasReachedStopsLimit?: boolean
    alreadySearchedAddresses?: JawgSuggestedFeature[]
    suggestedAddresses?: JawgSuggestedFeature[]
}>()

const emits = defineEmits<{
    addStop: []
    removeStop: []
    reverseRoute: []
    selectAddress: [address: JawgSuggestedFeature]
    updateAddressQuery: [query: string]
}>()

const value = defineModel<string>({
    required: true
})

const inputIcon = computed(() => {
    switch (type) {
        case CheckpointInputType.START:
            return 'IconFlag'
        case CheckpointInputType.END:
            return 'IconFlagCheckLeft'
        default:
            return 'IconMenuAlt4'
    }
})

const placeholderKey = computed(() => {
    switch (type) {
        case CheckpointInputType.START:
            return 'map.route_panel.checkpoint.start'
        case CheckpointInputType.END:
            return 'map.route_panel.checkpoint.end'
        default:
            return 'map.route_panel.checkpoint.stop'
    }
})

const startButtonStyles = computed(() => {
    return hasReachedStopsLimit ? 'bg-zinc-200' : 'bg-sky-500'
})

const startButtonIconStyles = computed(() => {
    return hasReachedStopsLimit ? 'text-zinc-400' : 'text-white'
})

const buttonIcon = computed(() => {
    switch (type) {
        case CheckpointInputType.START:
            return 'IconPlus'
        case CheckpointInputType.END:
            return 'IconSwitchVertical'
        default:
            return 'IconX'
    }
})

const handleButtonAction = () => {
    switch (type) {
        case CheckpointInputType.START:
            emits('addStop')
            break
        case CheckpointInputType.END:
            emits('reverseRoute')
            break
        default:
            emits('removeStop')
    }
}
</script>

<template>
    <div class="relative w-full flex items-center gap-1 md:gap-2">
        <AddressSearch
            v-model="value"
            :already-searched-addresses
            :suggested-addresses
            :icon="inputIcon"
            :placeholder="$t(placeholderKey)"
            is-route-checkpoint
            @selectAddress="$emit('selectAddress', $event)"
            @updateAddressQuery="$emit('updateAddressQuery', $event)"
        />

        <div class="ml-auto flex items-center gap-1">
            <button
                type="button"
                @click="handleButtonAction"
                class="size-10 flex items-center justify-center rounded-lg"
                :class="
                    type === CheckpointInputType.START
                        ? startButtonStyles
                        : 'bg-white border border-zinc-700'
                "
                :disabled="
                    type === CheckpointInputType.START && hasReachedStopsLimit
                "
            >
                <component
                    :is="buttonIcon"
                    class="size-6"
                    :class="
                        type === CheckpointInputType.START
                            ? startButtonIconStyles
                            : 'text-zinc-700'
                    "
                />
            </button>
        </div>
    </div>
</template>
